import { useState, useEffect, useMemo, useRef, KeyboardEvent } from "react";
import { effectiveScopeMode, scopeIds } from "@shared/messaging-rule-scope";
import { ExplainerVideoButton } from "@/components/explainer-video-button";
import { GuideButton } from "@/components/guide-button";
import { GuideNudgeBanner } from "@/components/guide-nudge-banner";
import { useQueryClient, useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest, parseErrorMessage } from "@/lib/queryClient";
import { useLocation, useParams, useSearch, Link } from "wouter";
import AppLayout from "@/components/app-layout";
import { ViewOnlyBanner, usePermissions } from "@/hooks/use-permissions";
import { useBetaAccess } from "@/hooks/use-beta-access";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ACCOUNT_TIMEZONES } from "@/lib/timezones";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { ChevronDown, Check, X, Paperclip, Loader2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import type { BookingRequestTemplate, CannedResponse, MessagingRule, Property, PropertyGroup } from "@shared/schema";
import { usePropertyGroups } from "@/components/property-group-select";
import {
  messageHasBookingRequestLinkToken,
  NEW_BOOKING_RULE_LINK_MISSING_WARNING,
} from "@shared/booking-request-link-token";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { usePageTitle } from "@/hooks/use-page-title";
import { isAbortError } from "@/hooks/use-cancellable-mutation";
import { annotateAttachmentUrl } from "@/lib/attachment-url";
import { EVENT_TRIGGER_OPTIONS, isComingSoonEventTrigger } from "@/lib/message-rule-event-triggers";
import { InsertPlaceholderDropdown } from "@/components/insert-placeholder-dropdown";
import { MessagePlaceholderPreview } from "@/components/message-placeholder-preview";
import { uploadMimeType } from "@/lib/upload-mime";
import { BookingRuleCatchUpDialog } from "@/components/booking-rule-catch-up-dialog";
import { useUnsavedDraft } from "@/hooks/use-unsaved-draft";
import type { BookingRuleCatchUpPreview } from "@shared/booking-rule-catch-up";

const FREQUENCY_OPTIONS = [
  { value: "daily", label: "Daily" },
  { value: "weekly", label: "Weekly" },
  { value: "biweekly", label: "Bi-weekly" },
  { value: "monthly", label: "Monthly" },
  { value: "quarterly", label: "Quarterly" },
  { value: "specific_date", label: "Specific Date" },
];

const DAY_OPTIONS = [
  { value: "monday", label: "Monday" },
  { value: "tuesday", label: "Tuesday" },
  { value: "wednesday", label: "Wednesday" },
  { value: "thursday", label: "Thursday" },
  { value: "friday", label: "Friday" },
  { value: "saturday", label: "Saturday" },
  { value: "sunday", label: "Sunday" },
];

const WEEK_OPTIONS = [
  { value: "first", label: "First" },
  { value: "second", label: "Second" },
  { value: "third", label: "Third" },
  { value: "fourth", label: "Fourth" },
  { value: "last", label: "Last" },
];

const RULE_ATTACHMENT_MAX_BYTES = 10 * 1024 * 1024; // 10MB, matches the server cap

// Shape returned by POST /api/app/messaging-rules/test-incoming (dry-run).
interface IncomingRuleTestResult {
  matched: boolean;
  confidence: number;
  reason: string | null;
  skippedNoContent: boolean;
  renderedReply: string | null;
  wouldSendAs: string;
  otherMatchingRules: { id: number; name: string; confidence: number }[];
}
interface IncomingRuleTestEntry {
  message: string;
  result: IncomingRuleTestResult;
}

// ─── Business Hours (Task #4707) ─────────────────────────────────────────────
type BusinessHoursDayState = { open: boolean; start: string; end: string };
type BusinessHoursDaysState = Record<string, BusinessHoursDayState>;

const BH_DAY_ORDER = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"] as const;
const BH_DAY_SHORT: Record<string, string> = {
  monday: "Mon", tuesday: "Tue", wednesday: "Wed", thursday: "Thu",
  friday: "Fri", saturday: "Sat", sunday: "Sun",
};

function defaultBusinessHoursDays(): BusinessHoursDaysState {
  const days: BusinessHoursDaysState = {};
  for (const d of BH_DAY_ORDER) {
    const weekend = d === "saturday" || d === "sunday";
    days[d] = { open: !weekend, start: "09:00", end: "17:00" };
  }
  return days;
}

function formatHhmm12(hhmm: string): string {
  const m = /^(\d{2}):(\d{2})$/.exec(hhmm);
  if (!m) return hhmm;
  let h = parseInt(m[1], 10);
  const min = m[2];
  const ampm = h >= 12 ? "pm" : "am";
  h = h % 12 || 12;
  return min === "00" ? `${h}${ampm}` : `${h}:${min}${ampm}`;
}

// Plain-language summary: consecutive days sharing an identical open/closed
// config are collapsed into a range, e.g. "Mon–Fri 9am–5pm, Sat 10am–2pm, Sun closed".
function summarizeBusinessHours(days: BusinessHoursDaysState): string {
  const keyFor = (d: string) => {
    const c = days[d];
    return c?.open ? `open:${c.start}-${c.end}` : "closed";
  };
  const parts: string[] = [];
  let i = 0;
  while (i < BH_DAY_ORDER.length) {
    let j = i;
    while (j + 1 < BH_DAY_ORDER.length && keyFor(BH_DAY_ORDER[j + 1]) === keyFor(BH_DAY_ORDER[i])) j++;
    const label = i === j
      ? BH_DAY_SHORT[BH_DAY_ORDER[i]]
      : `${BH_DAY_SHORT[BH_DAY_ORDER[i]]}–${BH_DAY_SHORT[BH_DAY_ORDER[j]]}`;
    const c = days[BH_DAY_ORDER[i]];
    parts.push(c?.open ? `${label} ${formatHhmm12(c.start)}–${formatHhmm12(c.end)}` : `${label} closed`);
    i = j + 1;
  }
  return parts.join(", ");
}

// Pull the original filename back out of a stored attachment URL's `?fn=`
// annotation for display; falls back to a generic label.
function fileNameFromAttachmentUrl(url: string): string {
  const qIdx = url.indexOf("?");
  if (qIdx < 0) return "Attached image";
  return new URLSearchParams(url.substring(qIdx + 1)).get("fn") || "Attached image";
}

export default function AppMessageRuleForm() {
  const params = useParams<{ id?: string }>();
  // A new rule identity gets fresh form defaults, including optional fields
  // absent from the next rule. Same-rule query refreshes keep the draft.
  return <MessageRuleForm key={params.id ?? "new"} />;
}

function MessageRuleForm() {
  const queryClient = useQueryClient();
  usePageTitle("Message Rule");
  const { canEdit: __canEditArea } = usePermissions();
  const readOnly = !__canEditArea("messages");
  // Task #4721 — keyword rules beta gate. Defaults false while the access
  // query loads (fail closed), so the radio can't be picked prematurely.
  const keywordRulesBeta = useBetaAccess("keyword_rules");
  const [, navigate] = useLocation();
  const params = useParams<{ id?: string }>();
  const search = useSearch();
  const isEdit = !!params.id;
  const { toast } = useToast();
  // Incoming Message rules are beta-gated: the grant unlocks the rule-type
  // radio and the in-form test playground. Pulse-prefilled incoming rules
  // still load/save for everyone (pre-existing flow) — they just stay dormant
  // until the owner is granted access, since the responder cron also checks
  // this grant server-side.
  const incomingBeta = useBetaAccess("incoming_message_rules");
  // Either grant unlocks the "Incoming Message (Keywords & Phrases)" radio —
  // keyword_rules (Task #4721 engine, supports approval mode + auto-send) or
  // incoming_message_rules (drafts-only semantic responder). The server
  // create/update gate accepts either as well.
  const keywordRulesGranted = keywordRulesBeta || incomingBeta;

  // Pulse "Create rule from this" CTA passes ?intentKey=...&suggestedKeywords=...
  // We use intentKey to fetch the cluster's sample messages + AI-drafted
  // reply from the server (URLs can't carry the full sample text safely),
  // and fall back to suggestedKeywords if the cluster has aged out.
  const { intentKey, suggestedKeywordsFromUrl } = useMemo(() => {
    const sp = new URLSearchParams(search);
    const raw = sp.get("suggestedKeywords") || "";
    const kw = raw.split(",").map(s => s.trim()).filter(Boolean);
    return {
      intentKey: sp.get("intentKey") || null,
      suggestedKeywordsFromUrl: kw,
    };
  }, [search]);

  // Tracks whether we've already applied the Pulse pre-fill, so we don't
  // clobber edits the host makes while the cluster query is in flight or
  // re-applies on re-renders.
  const prefillAppliedRef = useRef(false);

  const [name, setName] = useState("");
  // If a create finishes while the host keeps editing, subsequent saves must
  // update that new rule rather than create another copy.
  const [savedRuleId, setSavedRuleId] = useState<string | null>(null);
  const [bookingCatchUp, setBookingCatchUp] = useState<{ ruleId: number; preview: BookingRuleCatchUpPreview } | null>(null);
  const [selectedPropertyIds, setSelectedPropertyIds] = useState<number[]>([]);
  // "individual" = pick specific properties; "group" = pick property groups
  const [propertyMode, setPropertyMode] = useState<"all" | "individual" | "group">("individual");
  const [excludedPropertyIds, setExcludedPropertyIds] = useState<number[]>([]);
  const [scopeRevision, setScopeRevision] = useState(0);
  const [selectedGroupIds, setSelectedGroupIds] = useState<number[]>([]);
  const [groupDropdownOpen, setGroupDropdownOpen] = useState(false);
  const [groupSearch, setGroupSearch] = useState("");
  const [propertySearch, setPropertySearch] = useState("");
  const [ruleType, setRuleType] = useState("event");
  const [eventTrigger, setEventTrigger] = useState("");
  const [messageRecipients, setMessageRecipients] = useState("trigger_person");
  // Task #4705 — booking_form_followup send time is fixed at the auto-approval
  // deadline − 2h (computed server-side), so the immediately/after-delay timing
  // controls don't apply to it and it always saves sendTiming="immediately".
  const isBookingFormFollowup = ruleType === "event" && eventTrigger === "booking_form_followup";
  const [messageContent, setMessageContent] = useState("");
  // Task #4813 — optional live link to a saved canned-response template.
  // When set (event rules only), the send-time content is the template's
  // CURRENT body; the inline messageContent is kept as a fallback.
  const [cannedResponseId, setCannedResponseId] = useState<number | null>(null);
  const messageContentRef = useRef<HTMLTextAreaElement>(null);
  // Confirmation dialog for saving a New Booking rule WITHOUT the
  // {{booking_request_link:<id>}} token — requires typing "proceed".
  const [missingLinkDialogOpen, setMissingLinkDialogOpen] = useState(false);
  const [missingLinkConfirmText, setMissingLinkConfirmText] = useState("");

  function insertPlaceholderIntoMessageContent(token: string) {
    const el = messageContentRef.current;
    if (!el) {
      setMessageContent((prev) => prev + token);
      return;
    }
    const start = el.selectionStart ?? messageContent.length;
    const end = el.selectionEnd ?? messageContent.length;
    const current = el.value;
    setMessageContent(current.slice(0, start) + token + current.slice(end));
    setTimeout(() => {
      el.focus();
      const cursor = start + token.length;
      el.setSelectionRange(cursor, cursor);
    }, 0);
  }
  const [messageApproval, setMessageApproval] = useState("send_automatically");
  const [sendTiming, setSendTiming] = useState("immediately");
  const [delayMinutes, setDelayMinutes] = useState<number | null>(null);
  const [delayUnit, setDelayUnit] = useState<"minutes" | "hours" | "days">("minutes");
  const [delayPreset, setDelayPreset] = useState<string>("");
  const [delayError, setDelayError] = useState<string>("");
  // B22 — optional quiet hours (account timezone) for auto-sent replies.
  const [quietEnabled, setQuietEnabled] = useState(false);
  const [quietStart, setQuietStart] = useState("21:00");
  const [quietEnd, setQuietEnd] = useState("08:00");
  const [propertyDropdownOpen, setPropertyDropdownOpen] = useState(false);

  const [scheduleFrequency, setScheduleFrequency] = useState<string>("");
  const [scheduleDayOfWeek, setScheduleDayOfWeek] = useState<string>("");
  const [scheduleWeekOfMonth, setScheduleWeekOfMonth] = useState<string>("");
  const [scheduleDate, setScheduleDate] = useState<string>("");
  const [scheduleTime, setScheduleTime] = useState<string>("06:00");
  const [scheduleTimezone, setScheduleTimezone] = useState<string>("");
  const [scheduleErrors, setScheduleErrors] = useState<Record<string, string>>({});

  // Task #4707 — Business Hours after-hours auto-reply config.
  const [bhDays, setBhDays] = useState<BusinessHoursDaysState>(defaultBusinessHoursDays);
  const [bhStartDate, setBhStartDate] = useState<string>("");
  const [bhEndDate, setBhEndDate] = useState<string>("");
  const [bhTimezone, setBhTimezone] = useState<string>("");
  const [bhErrors, setBhErrors] = useState<Record<string, string>>({});

  const [keywords, setKeywords] = useState<string[]>([]);
  const [keywordInput, setKeywordInput] = useState<string>("");
  const keywordInputRef = useRef<HTMLInputElement>(null);

  // Test playground (incoming rules, beta) — the host types a pretend inbound
  // member message and the server dry-runs the SAME classify+render pipeline
  // the responder cron uses. Session-only history so keyword tweaks can be
  // compared without retyping; nothing is persisted or sent.
  const [testMessage, setTestMessage] = useState("");
  const [testHistory, setTestHistory] = useState<IncomingRuleTestEntry[]>([]);

  // Single optional image attached to the rule; included on every send.
  const [attachmentImageUrl, setAttachmentImageUrl] = useState<string>("");
  const [attachmentName, setAttachmentName] = useState<string>("");
  const [attachmentPreviewSrc, setAttachmentPreviewSrc] = useState<string>("");
  const [attachmentUploading, setAttachmentUploading] = useState(false);
  const [attachmentError, setAttachmentError] = useState<string>("");
  const attachmentInputRef = useRef<HTMLInputElement>(null);
  const attachmentAbortRef = useRef<AbortController | null>(null);
  const attachmentBlobUrlRef = useRef<string | null>(null);

  // Task #2439 — id of the sample cluster member used to render the
  // Pulse-drafted reply preview. Null until the host picks one (or the
  // cluster's first member is auto-selected once the cluster loads).
  const [previewMemberId, setPreviewMemberId] = useState<number | null>(null);

  const propertiesQuery = useQuery<Property[]>({ queryKey: ["/api/app/properties"] });
  const propertyGroupsQuery = usePropertyGroups();
  // Booking-request templates feed the "Booking form: <name>" placeholder
  // options — inserting one adds a {{booking_request_link:<id>}} token that
  // the server resolves at send time to the template's current active share
  // link (plus the member-identity ?m= param).
  const bookingTemplatesQuery = useQuery<BookingRequestTemplate[]>({ queryKey: ["/api/app/booking-requests/templates"] });
  // Task #4813 — saved templates library for the event-rule "use a saved
  // template" picker.
  const cannedResponsesQuery = useQuery<CannedResponse[]>({
    queryKey: ["/api/app/canned-responses"],
    enabled: ruleType === "event",
  });
  const linkedTemplate = useMemo(
    () => (cannedResponseId != null ? cannedResponsesQuery.data?.find((c) => c.id === cannedResponseId) ?? null : null),
    [cannedResponseId, cannedResponsesQuery.data],
  );
  // The content the dispatcher will actually send: linked template body when
  // available, otherwise the inline text.
  const effectiveMessageContent = linkedTemplate ? linkedTemplate.body : messageContent;
  const bookingLinkPlaceholderOptions = (bookingTemplatesQuery.data ?? [])
    .filter((t) => t.isActive !== false)
    .map((t) => ({
      label: `Booking form: ${t.name}`,
      token: `{{booking_request_link:${t.id}}}`,
    }));
  const properties = propertiesQuery.data || [];
  const activeProperties = useMemo(
    () =>
      [...properties]
        .filter(p => p.status !== "archive")
        .sort((a, b) => (a.name ?? "").localeCompare(b.name ?? "", undefined, { sensitivity: "base" })),
    [properties],
  );
  const activePropertyIds = useMemo(() => new Set(activeProperties.map(p => p.id)), [activeProperties]);
  const activeSelectedIds = useMemo(() => selectedPropertyIds.filter(id => activePropertyIds.has(id)), [selectedPropertyIds, activePropertyIds]);
  const normalizedPropertySearch = propertySearch.trim().toLowerCase();
  const filteredProperties = activeProperties.filter(p =>
    String(p.name ?? "").toLowerCase().includes(normalizedPropertySearch) ||
    String(p.address ?? "").toLowerCase().includes(normalizedPropertySearch));
  const groups = [...(propertyGroupsQuery.data ?? [])].sort((a, b) =>
    (a.name ?? "").localeCompare(b.name ?? "", undefined, { sensitivity: "base" }));
  const filteredGroups = groups.filter(g => String(g.name ?? "").toLowerCase().includes(groupSearch.trim().toLowerCase()));

  const ruleQuery = useQuery<MessagingRule>({
    queryKey: ["/api/app/messaging-rules", params.id],
    enabled: isEdit,
  });

  interface PulseClusterSampleMember {
    id: number;
    fullName: string;
    firstName: string;
  }
  interface PulseClusterPrefill {
    intentKey: string;
    keywords: string[];
    sampleMessages: string[];
    sampleInternalNotes: string[];
    sampleMemberNames: string[];
    sampleMembers: PulseClusterSampleMember[];
    distinctMemberCount: number;
    totalMessageCount: number;
    draftReply: string;
    suggestedName: string;
  }
  // Only fetch the Pulse pre-fill on the create flow — editing an existing
  // rule should never re-pull cluster data.
  const intentClusterQuery = useQuery<PulseClusterPrefill>({
    queryKey: ["/api/app/pulse/intent-cluster", intentKey],
    enabled: !isEdit && !!intentKey,
    retry: false,
  });

  // Pre-fill from Pulse: when intentKey resolves, populate keywords +
  // canned reply + a suggested rule name. When the cluster has aged out
  // (404) we still pre-fill keywords from the URL fallback so the host
  // isn't left with an empty form.
  useEffect(() => {
    if (isEdit) return;
    if (prefillAppliedRef.current) return;
    if (intentKey && intentClusterQuery.isLoading) return; // wait for fetch
    const cluster = intentClusterQuery.data;
    const keywordsToUse = cluster?.keywords ?? suggestedKeywordsFromUrl;
    if (keywordsToUse.length === 0) return;
    prefillAppliedRef.current = true;
    setRuleType("incoming_keywords");
    setKeywords(prev => (prev.length === 0 ? keywordsToUse : prev));
    if (cluster) {
      setName(prev => (prev ? prev : cluster.suggestedName));
      setMessageContent(prev => (prev ? prev : cluster.draftReply));
      // Default the preview dropdown to the first sample member so the
      // host sees a rendered example immediately (Task #2439).
      if (cluster.sampleMembers && cluster.sampleMembers.length > 0) {
        setPreviewMemberId(prev => prev ?? cluster.sampleMembers[0].id);
      }
    } else {
      setName(prev => (prev ? prev : `Auto-reply: ${keywordsToUse.join(" / ")}`));
    }
  }, [isEdit, intentKey, intentClusterQuery.isLoading, intentClusterQuery.data, suggestedKeywordsFromUrl]);

  const ruleInitializedRef = useRef(false);
  const [draftInitialized, setDraftInitialized] = useState(!isEdit);
  useEffect(() => {
    if (isEdit && ruleQuery.data && !ruleInitializedRef.current) {
      ruleInitializedRef.current = true;
      const r = ruleQuery.data;
      setExcludedPropertyIds(scopeIds(r.excludedPropertyIds));
      setScopeRevision(r.scopeRevision);
      setName(r.name || "");
      setRuleType(r.ruleType || "event");
      setEventTrigger(r.eventTrigger || "");
      setMessageRecipients(r.messageRecipients || "trigger_person");
      setMessageContent(r.messageContent || "");
      setCannedResponseId(r.cannedResponseId ?? null);
      setMessageApproval(r.messageApproval || "send_automatically");
      setSendTiming(r.sendTiming || "immediately");
      if (r.quietHours && typeof r.quietHours === "object") {
        setQuietEnabled(true);
        setQuietStart(r.quietHours.start || "21:00");
        setQuietEnd(r.quietHours.end || "08:00");
      }
      if (r.delayMinutes != null) {
        const preset = [5, 15, 30, 60, 120].includes(r.delayMinutes) ? String(r.delayMinutes) : "custom";
        setDelayPreset(preset);
        setDelayMinutes(r.delayMinutes);
        if (r.delayMinutes % 1440 === 0) setDelayUnit("days");
        else if (r.delayMinutes % 60 === 0) setDelayUnit("hours");
        else setDelayUnit("minutes");
      }
      if (r.propertyGroupIds) {
        try {
          const ids = r.propertyGroupIds.split(",").map((s: string) => parseInt(s.trim())).filter((n: number) => !isNaN(n));
          setSelectedGroupIds(ids);
          setPropertyMode("group");
        } catch {
          setSelectedGroupIds([]);
          setPropertyMode("group");
        }
      } else if (r.properties) {
        try {
          const ids = r.properties.split(",").map((s: string) => parseInt(s.trim())).filter((n: number) => !isNaN(n));
          setSelectedPropertyIds(ids);
          setPropertyMode("individual");
        } catch {
          setSelectedPropertyIds([]);
          setPropertyMode("individual");
        }
      }
      setScheduleFrequency(r.scheduleFrequency || "");
      setPropertyMode(effectiveScopeMode(r));
      setScheduleDayOfWeek(r.scheduleDayOfWeek || "");
      setScheduleWeekOfMonth(r.scheduleWeekOfMonth || "");
      setScheduleDate(r.scheduleDate || "");
      setScheduleTime(r.scheduleTime || "06:00");
      setScheduleTimezone(r.scheduleTimezone || "");
      setKeywords(r.keywords || []);
      if (r.businessHours) {
        const bh = r.businessHours;
        const days = defaultBusinessHoursDays();
        for (const d of BH_DAY_ORDER) {
          if (bh.days?.[d]) days[d] = { ...bh.days[d] };
        }
        setBhDays(days);
        setBhStartDate(bh.startDate || "");
        setBhEndDate(bh.endDate || "");
        setBhTimezone(bh.timezone || "");
      }
      setAttachmentImageUrl(r.attachmentImageUrl || "");
      setAttachmentPreviewSrc(r.attachmentImageUrl || "");
      setAttachmentName(r.attachmentImageUrl ? fileNameFromAttachmentUrl(r.attachmentImageUrl) : "");
      setAttachmentError("");
      setDraftInitialized(true);
    }
  }, [isEdit, ruleQuery.data]);

  // Only draft fields count, not searches, open popovers, or test previews.
  // The baseline is captured after the one-time edit hydration, never from a
  // later server refresh. Reverting all edits therefore makes the form clean.
  const draftSnapshot = JSON.stringify({
    name, selectedPropertyIds, propertyMode, selectedGroupIds, excludedPropertyIds, ruleType,
    eventTrigger, messageRecipients, messageContent, cannedResponseId,
    messageApproval, sendTiming, delayMinutes, delayUnit, delayPreset,
    scheduleFrequency, scheduleDayOfWeek, scheduleWeekOfMonth, scheduleDate,
    scheduleTime, scheduleTimezone, bhDays, bhStartDate, bhEndDate, bhTimezone,
    keywords, keywordInput, attachmentImageUrl, quietEnabled, quietStart, quietEnd,
  });
  const [savedDraftSnapshot, setSavedDraftSnapshot] = useState<string | null>(null);
  const currentDraftRef = useRef({ snapshot: draftSnapshot, attachmentUploading });
  currentDraftRef.current = { snapshot: draftSnapshot, attachmentUploading };
  useEffect(() => {
    if (draftInitialized && savedDraftSnapshot === null) {
      setSavedDraftSnapshot(draftSnapshot);
    }
  }, [draftInitialized, savedDraftSnapshot, draftSnapshot]);
  const clearLeaveGuard = useUnsavedDraft(
    !readOnly && savedDraftSnapshot !== null &&
    (draftSnapshot !== savedDraftSnapshot || attachmentUploading),
  );

  // Revoke any object-URL blob preview on unmount to avoid a memory leak.
  useEffect(() => {
    return () => {
      if (attachmentBlobUrlRef.current) URL.revokeObjectURL(attachmentBlobUrlRef.current);
    };
  }, []);

  // Task #2439 — render {{member.firstName}} / {{member.fullName}} /
  // {{member.name}} placeholders against the picked sample member. We
  // accept a couple of name variants because the Pulse-drafted reply
  // most commonly uses {{member.firstName}} but hosts may hand-edit to
  // use full name instead. Anything we don't recognize is left as-is so
  // the host can still see unresolved placeholders in the preview.
  function renderPlaceholders(content: string, member: PulseClusterSampleMember | null): string {
    if (!member) return content;
    const fullName = member.fullName?.trim() || member.firstName || "";
    const firstName = member.firstName?.trim() || fullName;
    return content.replace(/\{\{\s*member\.([a-zA-Z]+)\s*\}\}/g, (match, prop: string) => {
      switch (prop.toLowerCase()) {
        case "firstname":
          return firstName;
        case "fullname":
        case "name":
          return fullName;
        default:
          return match;
      }
    });
  }

  const previewMember = useMemo<PulseClusterSampleMember | null>(() => {
    if (previewMemberId == null) return null;
    return intentClusterQuery.data?.sampleMembers?.find(m => m.id === previewMemberId) ?? null;
  }, [previewMemberId, intentClusterQuery.data]);

  const selectedPropertiesLabel = useMemo(() => {
    if (activeSelectedIds.length === 0) return "0 properties selected";
    if (activeSelectedIds.length === activeProperties.length && activeProperties.length > 0) return "All properties selected";
    return `${activeSelectedIds.length} ${activeSelectedIds.length === 1 ? "property" : "properties"} selected`;
  }, [activeSelectedIds, activeProperties]);

  const saveMutation = useMutation({
    mutationFn: async ({ data }: { data: Record<string, unknown>; draftSnapshot: string }) => {
      const ruleId = params.id ?? savedRuleId;
      if (ruleId) {
        const res = await apiRequest("PATCH", `/api/app/messaging-rules/${ruleId}`, {
          ...data, offerBookingCatchUp: true,
        });
        return res.json();
      } else {
        const res = await apiRequest("POST", "/api/app/messaging-rules", data);
        return res.json();
      }
    },
    onSuccess: (saved: MessagingRule & { bookingCatchUp?: BookingRuleCatchUpPreview }, submitted) => {
      setScopeRevision(saved.scopeRevision);
      const hasNewerEdits = currentDraftRef.current.snapshot !== submitted.draftSnapshot ||
        currentDraftRef.current.attachmentUploading;
      const submittedDraft = JSON.parse(submitted.draftSnapshot);
      const reconcileIds = (current: number[], before: number[], savedIds: number[]) => {
        const beforeSet = new Set(before);
        const currentSet = new Set(current);
        return [...new Set([
          ...savedIds.filter(id => !beforeSet.has(id) || currentSet.has(id)),
          ...current.filter(id => !beforeSet.has(id)),
        ])];
      };
      // Apply server cleanup without overwriting edits made while the save ran.
      const savedSelected = scopeIds(saved.properties);
      const savedExcluded = scopeIds(saved.excludedPropertyIds);
      setSelectedPropertyIds(current => reconcileIds(current, submittedDraft.selectedPropertyIds, savedSelected));
      setExcludedPropertyIds(current => reconcileIds(current, submittedDraft.excludedPropertyIds, savedExcluded));
      setSavedDraftSnapshot(JSON.stringify({
        ...submittedDraft, selectedPropertyIds: savedSelected, excludedPropertyIds: savedExcluded,
      }));
      setSavedRuleId(String(saved.id));
      if (!hasNewerEdits) clearLeaveGuard();
      queryClient.invalidateQueries({ queryKey: ["/api/app/messaging-rules"] });
      toast({
        title: isEdit || savedRuleId ? "Rule updated successfully" : "Rule created successfully",
        ...(hasNewerEdits
          ? { description: "Your newer edits are still unsaved." }
          : !isEdit && !savedRuleId && saved.enabled === false
            ? { description: "New rules start switched off. Turn it on in your rules list when you're ready." }
            : {}),
      });
      if (saved.bookingCatchUp) {
        setBookingCatchUp({ ruleId: saved.id, preview: saved.bookingCatchUp });
      } else if (!hasNewerEdits) {
        navigate("/app/messages?tab=rules");
      }
    },
    onError: (err: unknown) =>
      toast({
        title: "Failed to save rule",
        description: parseErrorMessage(err, "Couldn't save this rule. Please try again."),
        variant: "destructive",
      }),
  });

  const testMutation = useMutation({
    mutationFn: async (message: string) => {
      const res = await apiRequest("POST", "/api/app/messaging-rules/test-incoming", {
        testMessage: message,
        keywords,
        messageContent: messageContent || null,
        ruleId: isEdit ? parseInt(params.id!, 10) : null,
      });
      return (await res.json()) as IncomingRuleTestResult;
    },
    onSuccess: (result, message) => {
      setTestHistory(prev => [{ message, result }, ...prev].slice(0, 5));
      setTestMessage("");
    },
    onError: (err: unknown) =>
      toast({
        title: "Test failed",
        description: parseErrorMessage(err, "Couldn't run the test. Please try again."),
        variant: "destructive",
      }),
  });

  function handleRunTest() {
    const message = testMessage.trim();
    if (!message) return;
    if (keywords.length === 0) {
      toast({ title: "Add at least one keyword before testing", variant: "destructive" });
      return;
    }
    testMutation.mutate(message);
  }

  function validateSchedule(): boolean {
    if (ruleType !== "scheduled") return true;
    const errs: Record<string, string> = {};
    if (!scheduleFrequency) {
      errs.scheduleFrequency = "Frequency is required";
    }
    if (!scheduleTime) {
      errs.scheduleTime = "Time is required";
    }
    if (scheduleFrequency === "weekly" || scheduleFrequency === "biweekly") {
      if (!scheduleDayOfWeek) errs.scheduleDayOfWeek = "Day is required";
    }
    if (scheduleFrequency === "monthly" || scheduleFrequency === "quarterly") {
      if (!scheduleWeekOfMonth) errs.scheduleWeekOfMonth = "Week is required";
      if (!scheduleDayOfWeek) errs.scheduleDayOfWeek = "Day is required";
    }
    if (scheduleFrequency === "specific_date") {
      if (!scheduleDate) errs.scheduleDate = "Date is required";
    }
    setScheduleErrors(errs);
    return Object.keys(errs).length === 0;
  }

  function handleSave() {
    if (!name.trim()) {
      toast({ title: "Rule name is required", variant: "destructive" });
      return;
    }
    if (attachmentUploading) {
      toast({
        title: "Image still uploading",
        description: "Please wait for the image to finish uploading before saving.",
        variant: "destructive",
      });
      return;
    }
    if (ruleType !== "scheduled" && !isBookingFormFollowup && sendTiming === "after_delay") {
      if (delayMinutes == null) {
        setDelayError("Delay duration is required");
        return;
      }
      if (delayMinutes < 1) {
        const unitLabel = delayUnit === "days" ? "day" : delayUnit === "hours" ? "hour" : "minute";
        setDelayError(`Delay must be at least 1 ${unitLabel}`);
        return;
      }
    }
    setDelayError("");
    if (!validateSchedule()) return;
    if (!validateBusinessHours()) return;

    const isScheduled = ruleType === "scheduled";
    const isIncomingKeywords = ruleType === "incoming_keywords";

    if (isIncomingKeywords && keywords.length === 0) {
      toast({ title: "At least one keyword or phrase is required", variant: "destructive" });
      return;
    }

    // Task #4705 — HARD block (no proceed dialog): a "Booking Form Not
    // Completed" rule exists solely to re-send the booking form before the
    // auto-approval deadline, so the link token is required. The server
    // rejects an enabled token-less rule too; this mirrors that up front.
    if (
      ruleType === "event" &&
      eventTrigger === "booking_form_followup" &&
      !messageHasBookingRequestLinkToken(effectiveMessageContent)
    ) {
      toast({
        title: "Booking request link required",
        description: "Booking Form Not Completed rules must include a booking request link so the member can complete the form before the auto-approval deadline. Use “Insert booking request link” in the message editor.",
        variant: "destructive",
      });
      return;
    }

    // A New Booking rule without the booking-form link can't pair the eventual
    // submission back to the member — allowed, but only after an explicit,
    // deliberate confirmation (the host must type "proceed" in the dialog).
    if (
      ruleType === "event" &&
      eventTrigger === "new_booking" &&
      !messageHasBookingRequestLinkToken(effectiveMessageContent)
    ) {
      setMissingLinkConfirmText("");
      setMissingLinkDialogOpen(true);
      return;
    }

    performSave();
  }

  function validateBusinessHours(): boolean {
    if (ruleType !== "business_hours") {
      setBhErrors({});
      return true;
    }
    const errs: Record<string, string> = {};
    const openDays = BH_DAY_ORDER.filter((d) => bhDays[d]?.open);
    if (openDays.length === 0) {
      errs.days = "At least one day must be open";
    }
    for (const d of openDays) {
      const c = bhDays[d];
      if (!c.start || !c.end) {
        errs[d] = "Opening and closing times are required";
      } else if (c.end <= c.start) {
        errs[d] = "Closing time must be after opening time";
      }
    }
    if (bhStartDate && bhEndDate && bhEndDate < bhStartDate) {
      errs.dateRange = "End date must be on or after the start date";
    }
    if (!messageContent.trim()) {
      errs.message = "An auto-reply message is required";
      toast({ title: "Auto-reply message is required", description: "Business Hours rules send this message to members who write outside your working hours.", variant: "destructive" });
    }
    setBhErrors(errs);
    return Object.keys(errs).length === 0;
  }

  function performSave() {
    const isScheduled = ruleType === "scheduled";
    const isIncomingKeywords = ruleType === "incoming_keywords";
    const isBusinessHours = ruleType === "business_hours";

    // "Coming soon" event triggers aren't functional yet, so any rule that uses
    // one is forced disabled on save and can't be re-enabled from the list until
    // the feature ships (see RulesTab enable guard).
    const isComingSoon = ruleType === "event" && isComingSoonEventTrigger(eventTrigger);

    saveMutation.mutate({ draftSnapshot, data: {
      name,
      scopeMode: propertyMode,
      scopeRevision,
      excludedPropertyIds: excludedPropertyIds.join(","),
      properties: propertyMode === "individual" && selectedPropertyIds.length > 0 ? selectedPropertyIds.join(",") : null,
      propertyGroupIds: propertyMode === "group" && selectedGroupIds.length > 0 ? selectedGroupIds.join(",") : null,
      ruleType,
      ...(isComingSoon ? { enabled: false } : {}),
      eventTrigger: !isScheduled && !isBusinessHours ? (eventTrigger || null) : null,
      messageRecipients: isScheduled ? "all_members" : isBusinessHours ? "trigger_person" : messageRecipients,
      copyMessageContentFrom: null,
      messageContent: messageContent || null,
      cannedResponseId: ruleType === "event" ? cannedResponseId : null,
      attachmentImageUrl: attachmentImageUrl || null,
      messageApproval: isBusinessHours ? "send_automatically" : messageApproval,
      // booking_form_followup timing is fixed (auto-approval deadline − 2h);
      // business_hours always immediate; scheduled rules always immediate.
      sendTiming: isScheduled || isBusinessHours || isBookingFormFollowup ? "immediately" : sendTiming,
      delayMinutes: !isScheduled && !isBusinessHours && !isBookingFormFollowup && sendTiming === "after_delay" ? delayMinutes : null,
      quietHours: !isScheduled && !isBusinessHours && messageApproval === "send_automatically" && quietEnabled && quietStart !== quietEnd
        ? { start: quietStart, end: quietEnd }
        : null,
      scheduleFrequency: isScheduled ? (scheduleFrequency || null) : null,
      scheduleDayOfWeek: isScheduled ? (scheduleDayOfWeek || null) : null,
      scheduleWeekOfMonth: isScheduled ? (scheduleWeekOfMonth || null) : null,
      scheduleDate: isScheduled ? (scheduleDate || null) : null,
      scheduleTime: isScheduled ? (scheduleTime || null) : null,
      scheduleTimezone: isScheduled ? (scheduleTimezone || null) : null,
      keywords: isIncomingKeywords ? keywords : null,
      businessHours: isBusinessHours
        ? {
            startDate: bhStartDate || null,
            endDate: bhEndDate || null,
            timezone: bhTimezone || null,
            days: Object.fromEntries(
              BH_DAY_ORDER.map((d) => [d, { open: !!bhDays[d]?.open, start: bhDays[d]?.start || "09:00", end: bhDays[d]?.end || "17:00" }]),
            ),
          }
        : null,
    } });
  }

  async function handleAttachmentFile(files: FileList | null) {
    const file = files?.[0];
    if (!file) return;
    setAttachmentError("");
    // Resolve before gating: a .heic pick reports an EMPTY file.type, so the
    // raw startsWith("image/") check rejected a photo the SERVER accepts
    // (MESSAGE_ATTACHMENT_MIME includes image/heic — same upload endpoint the
    // message composers use).
    const resolvedMime = uploadMimeType(file);
    if (!resolvedMime.startsWith("image/")) {
      setAttachmentError("Only image files can be attached");
      if (attachmentInputRef.current) attachmentInputRef.current.value = "";
      return;
    }
    if (file.size > RULE_ATTACHMENT_MAX_BYTES) {
      setAttachmentError(`Image is over ${Math.round(RULE_ATTACHMENT_MAX_BYTES / (1024 * 1024))}MB`);
      if (attachmentInputRef.current) attachmentInputRef.current.value = "";
      return;
    }
    // Supersede any in-flight upload.
    attachmentAbortRef.current?.abort();
    const controller = new AbortController();
    attachmentAbortRef.current = controller;
    setAttachmentUploading(true);
    // Show a local blob preview until the object is made public on save.
    const blobUrl = URL.createObjectURL(file);
    if (attachmentBlobUrlRef.current) URL.revokeObjectURL(attachmentBlobUrlRef.current);
    attachmentBlobUrlRef.current = blobUrl;
    setAttachmentPreviewSrc(blobUrl);
    setAttachmentName(file.name);
    setAttachmentImageUrl("");
    try {
      const urlRes = await apiRequest(
        "POST",
        "/api/app/messages/upload-url",
        {},
        { signal: controller.signal },
      );
      const { uploadUrl, objectPath } = (await urlRes.json()) as {
        uploadUrl: string;
        objectPath: string;
      };
      const putRes = await fetch(uploadUrl, {
        method: "PUT",
        headers: { "Content-Type": resolvedMime },
        body: file,
        signal: controller.signal,
      });
      if (!putRes.ok) throw new Error(`Upload failed (${putRes.status})`);
      setAttachmentImageUrl(annotateAttachmentUrl(objectPath, file.name));
    } catch (err: unknown) {
      if (!isAbortError(err)) {
        setAttachmentError(parseErrorMessage(err, "Upload failed"));
        setAttachmentImageUrl("");
        setAttachmentPreviewSrc("");
        setAttachmentName("");
        if (attachmentBlobUrlRef.current === blobUrl) {
          URL.revokeObjectURL(blobUrl);
          attachmentBlobUrlRef.current = null;
        }
      }
    } finally {
      if (attachmentAbortRef.current === controller) {
        attachmentAbortRef.current = null;
        setAttachmentUploading(false);
      }
      if (attachmentInputRef.current) attachmentInputRef.current.value = "";
    }
  }

  function removeAttachment() {
    attachmentAbortRef.current?.abort();
    attachmentAbortRef.current = null;
    if (attachmentBlobUrlRef.current) {
      URL.revokeObjectURL(attachmentBlobUrlRef.current);
      attachmentBlobUrlRef.current = null;
    }
    setAttachmentImageUrl("");
    setAttachmentName("");
    setAttachmentPreviewSrc("");
    setAttachmentError("");
    setAttachmentUploading(false);
    if (attachmentInputRef.current) attachmentInputRef.current.value = "";
  }

  function addKeyword(raw: string) {
    const trimmed = raw.trim().replace(/,$/, "").trim();
    if (!trimmed) return;
    setKeywords(prev => prev.includes(trimmed) ? prev : [...prev, trimmed]);
    setKeywordInput("");
  }

  function handleKeywordKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      addKeyword(keywordInput);
    } else if (e.key === "Backspace" && keywordInput === "" && keywords.length > 0) {
      setKeywords(prev => prev.slice(0, -1));
    }
  }

  function removeKeyword(kw: string) {
    setKeywords(prev => prev.filter(k => k !== kw));
  }

  function toggleProperty(id: number) {
    setExcludedPropertyIds(prev => selectedPropertyIds.includes(id)
      ? [...new Set([...prev, id])] : prev.filter(p => p !== id));
    setSelectedPropertyIds(prev =>
      prev.includes(id) ? prev.filter(p => p !== id) : [...prev, id]
    );
  }

  function clearScheduleErrors(field: string) {
    setScheduleErrors(prev => {
      const next = { ...prev };
      delete next[field];
      return next;
    });
  }

  if (isEdit && ruleQuery.isLoading) {
    return (
      <AppLayout>
        <div className="p-6 max-w-6xl mx-auto space-y-6">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-96 w-full" />
        </div>
      </AppLayout>
    );
  }

  const isScheduled = ruleType === "scheduled";
  const pulseCluster = !isEdit ? intentClusterQuery.data : undefined;
  const pulseLoading = !isEdit && !!intentKey && intentClusterQuery.isLoading;
  // Task #2439 — the cluster has "aged out" when the host arrived via
  // an intentKey link but the server returned 404 (cluster no longer
  // active in the 14-day window). In that case we still want to render
  // the Pulse banner so we can surface a friendly "preview disabled"
  // note alongside whatever URL-derived keywords are available.
  const pulseAgedOut =
    !isEdit && !!intentKey && !intentClusterQuery.isLoading && !intentClusterQuery.data;
  const needsDay = scheduleFrequency === "weekly" || scheduleFrequency === "biweekly" || scheduleFrequency === "monthly" || scheduleFrequency === "quarterly";
  const needsWeek = scheduleFrequency === "monthly" || scheduleFrequency === "quarterly";
  const needsDate = scheduleFrequency === "specific_date";

  return (
    <AppLayout>
      <div className="p-6 max-w-6xl mx-auto">
        <ViewOnlyBanner area="messages" />
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900" data-testid="text-rule-form-title" data-tour="message-rule-form-heading">
            {isEdit || savedRuleId ? "Edit Message Rule" : "Create Message Rule"}
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Set up automated message rules to manage how PadPulse sends scheduled messages. Define when messages are sent, what they say, and which event triggers them so your communication always stays timely and consistent.
          </p>
        </div>

        <div className="flex justify-end gap-3 mb-6">
          <Button
            variant="outline"
            asChild
            data-testid="button-cancel-rule"
          >
            <Link href="/app/messages?tab=rules">Cancel</Link>
          </Button>
          <Button
            onClick={handleSave}
            disabled={readOnly || saveMutation.isPending || bookingCatchUp != null}
            data-testid="button-save-rule"
          >
            {saveMutation.isPending ? "Saving..." : "Save Rule"}
          </Button>
          <div className="flex items-center gap-2">
            <ExplainerVideoButton pageKey="message-rule-form" />
            <GuideButton pageKey="message-rule-form" />
          </div>
        </div>
        <GuideNudgeBanner pageKey="message-rule-form" />

        {bookingCatchUp && (
          <BookingRuleCatchUpDialog
            ruleId={bookingCatchUp.ruleId}
            preview={bookingCatchUp.preview}
            onFinish={() => {
              setBookingCatchUp(null);
              if (currentDraftRef.current.snapshot === savedDraftSnapshot &&
                  !currentDraftRef.current.attachmentUploading) {
                clearLeaveGuard();
                navigate("/app/messages?tab=rules");
              }
            }}
          />
        )}
        <Dialog
          open={missingLinkDialogOpen}
          onOpenChange={(open) => {
            // Gate dismissal on the in-flight save (repo dialog convention).
            if (!open && saveMutation.isPending) return;
            setMissingLinkDialogOpen(open);
          }}
        >
          <DialogContent
            className="max-h-[90vh] overflow-y-auto"
            onInteractOutside={(e) => {
              if (saveMutation.isPending) e.preventDefault();
            }}
            onEscapeKeyDown={(e) => {
              if (saveMutation.isPending) e.preventDefault();
            }}
            data-testid="dialog-missing-booking-link"
          >
            <DialogHeader>
              <DialogTitle>Save without a booking request link?</DialogTitle>
              <DialogDescription data-testid="text-missing-booking-link-warning">
                {NEW_BOOKING_RULE_LINK_MISSING_WARNING}
              </DialogDescription>
            </DialogHeader>
            <div className="bg-blue-50 border border-blue-200 rounded-md p-3 text-sm space-y-1.5">
              <p className="font-medium text-blue-900">Didn't mean to do this?</p>
              <p className="text-blue-800">
                Insert a dynamic placeholder for a booking request from the dropdown next to the message content, or{" "}
                <Link
                  href="/app/members/booking-requests"
                  className="underline font-medium"
                  onClick={() => setMissingLinkDialogOpen(false)}
                >
                  go to Booking Requests
                </Link>{" "}
                to create one.
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="missing-link-confirm-input">
                Type <span className="font-semibold">proceed</span> to confirm
              </Label>
              <Input
                id="missing-link-confirm-input"
                value={missingLinkConfirmText}
                onChange={(e) => setMissingLinkConfirmText(e.target.value)}
                placeholder="proceed"
                autoComplete="off"
                data-testid="input-missing-booking-link-confirm"
              />
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setMissingLinkDialogOpen(false)}
                disabled={saveMutation.isPending}
                data-testid="button-missing-booking-link-cancel"
              >
                Cancel
              </Button>
              <Button
                onClick={() => {
                  setMissingLinkDialogOpen(false);
                  performSave();
                }}
                disabled={
                  missingLinkConfirmText.trim().toLowerCase() !== "proceed" ||
                  saveMutation.isPending
                }
                data-testid="button-missing-booking-link-accept"
              >
                {saveMutation.isPending ? "Saving..." : "Accept"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {(pulseLoading || pulseCluster || pulseAgedOut || (!isEdit && !intentKey && suggestedKeywordsFromUrl.length > 0)) && (
          <div
            className="mb-6 border border-blue-200 rounded-lg p-4 bg-blue-50"
            data-testid="banner-pulse-prefill"
          >
            <div className="flex items-start gap-3">
              <div className="flex-1">
                <h3 className="text-sm font-semibold text-blue-900">
                  PadPulse pre-filled this rule from a repeat-intent cluster
                </h3>
                {pulseLoading && (
                  <p className="text-xs text-blue-800 mt-1" data-testid="text-pulse-loading">
                    Loading sample messages and drafting a reply…
                  </p>
                )}
                {pulseCluster && (
                  <>
                    <p className="text-xs text-blue-800 mt-1">
                      {pulseCluster.distinctMemberCount > 0
                        ? `${pulseCluster.distinctMemberCount} member${pulseCluster.distinctMemberCount === 1 ? "" : "s"} sent ${pulseCluster.totalMessageCount} message${pulseCluster.totalMessageCount === 1 ? "" : "s"} about "${pulseCluster.keywords.join(" + ")}" in the last 14 days.`
                        : `Your team kept noting "${pulseCluster.keywords.join(" + ")}" across ${pulseCluster.totalMessageCount} message${pulseCluster.totalMessageCount === 1 ? "" : "s"} in the last 14 days.`}
                      {" "}The trigger keywords and a draft reply are filled in below — edit them before saving.
                    </p>
                    {pulseCluster.sampleMessages.length > 0 && (
                      <div className="mt-3">
                        <p className="text-xs font-semibold text-blue-900 mb-1">Sample messages PadPulse grouped:</p>
                        <ul className="space-y-1" data-testid="list-pulse-samples">
                          {pulseCluster.sampleMessages.map((s, i) => (
                            <li
                              key={`sample-${i}`}
                              className="text-xs text-blue-900 italic bg-white/70 border border-blue-100 rounded px-2 py-1"
                              data-testid={`text-pulse-sample-${i}`}
                            >
                              "{s}"
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {/* Task #2439 — "Preview as…" lets the host see the
                        Drafted reply with placeholders resolved
                        against a real cluster member before saving. */}
                    <div className="mt-3" data-testid="pulse-preview-reply">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="text-xs font-semibold text-blue-900">Preview reply as:</p>
                        {pulseCluster.sampleMembers && pulseCluster.sampleMembers.length > 0 ? (
                          <Select
                            value={previewMemberId != null ? String(previewMemberId) : ""}
                            onValueChange={v => setPreviewMemberId(parseInt(v, 10))}
                          >
                            <SelectTrigger
                              className="h-7 w-auto min-w-[160px] text-xs bg-white"
                              data-testid="select-pulse-preview-member"
                            >
                              <SelectValue placeholder="Pick a sample member" />
                            </SelectTrigger>
                            <SelectContent>
                              {pulseCluster.sampleMembers.map(m => (
                                <SelectItem key={m.id} value={String(m.id)}>
                                  {m.fullName || m.firstName || `Member #${m.id}`}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        ) : (
                          <span
                            className="text-xs italic text-blue-800"
                            data-testid="text-pulse-preview-unavailable"
                          >
                            Preview unavailable — the original cluster members have aged out.
                          </span>
                        )}
                      </div>
                      {previewMember && messageContent && (
                        <div className="mt-2" data-testid="pulse-preview-bubble-wrapper">
                          <p
                            className="text-[11px] text-blue-800 mb-1"
                            data-testid="text-pulse-preview-recipient"
                          >
                            To {previewMember.fullName || previewMember.firstName || `Member #${previewMember.id}`}
                          </p>
                          <div className="flex justify-end">
                            <div
                              className="max-w-[80%] rounded-lg bg-blue-500 text-white px-3 py-2 text-sm whitespace-pre-wrap break-words shadow-sm max-h-64 overflow-y-auto"
                              data-testid="text-pulse-preview-rendered"
                            >
                              {renderPlaceholders(messageContent, previewMember)}
                            </div>
                          </div>
                        </div>
                      )}
                      {previewMember && !messageContent && (
                        <p className="mt-2 text-xs italic text-blue-800" data-testid="text-pulse-preview-empty">
                          Add message content above to see it rendered for {previewMember.firstName || previewMember.fullName}.
                        </p>
                      )}
                    </div>
                    {pulseCluster.sampleInternalNotes.length > 0 && (
                      <div className="mt-2">
                        <p className="text-xs font-semibold text-blue-900 mb-1">Internal notes your team wrote:</p>
                        <ul className="space-y-1" data-testid="list-pulse-internal-notes">
                          {pulseCluster.sampleInternalNotes.map((s, i) => (
                            <li
                              key={`note-${i}`}
                              className="text-xs text-blue-900 italic bg-white/70 border border-blue-100 rounded px-2 py-1"
                              data-testid={`text-pulse-internal-note-${i}`}
                            >
                              "{s}"
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </>
                )}
                {!pulseLoading && !pulseCluster && (pulseAgedOut || suggestedKeywordsFromUrl.length > 0) && (
                  <>
                    <p className="text-xs text-blue-800 mt-1" data-testid="text-pulse-fallback">
                      {suggestedKeywordsFromUrl.length > 0
                        ? "The original cluster has aged out, but PadPulse pre-filled the keyword trigger from the link. Edit the draft reply below."
                        : "The original cluster has aged out, so PadPulse couldn't pre-fill keywords or a draft reply. Add them by hand below."}
                    </p>
                    {/* Task #2439 — aged-out clusters have no sample
                        members to preview against, so we tell the host
                        explicitly that the preview is unavailable. */}
                    <p
                      className="text-xs italic text-blue-800 mt-2"
                      data-testid="text-pulse-preview-unavailable"
                    >
                      Preview unavailable — the original cluster members have aged out, so there's no real member to render placeholders against.
                    </p>
                  </>
                )}
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="border rounded-lg p-6 bg-white">
            <h2 className="text-lg font-semibold text-gray-900 mb-5" data-testid="text-settings-heading">
              Message Rule Settings
            </h2>

            <div className="space-y-5">
              <div>
                <Label htmlFor="rule-name" className="text-sm font-medium text-gray-700">Rule Name</Label>
                <Input
                  id="rule-name"
                  value={name}
                  onChange={e => setName(e.target.value)}
                  placeholder="e.g. Welcome Message"
                  className="mt-1.5"
                  data-testid="input-rule-name"
                />
              </div>

              <div data-tour="message-rule-form-property-selector">
                <Label className="text-sm font-medium text-gray-700">Send This Message To</Label>
                <p className="text-xs text-gray-500 mt-0.5 mb-2">Choose individual properties or a property group. Group-scoped rules automatically include new properties added to the group later.</p>
                {/* Mode toggle */}
                <div className="flex rounded-md border overflow-hidden mb-2 text-sm" data-testid="property-mode-toggle">
                  <button type="button" data-testid="button-property-mode-all" className={`flex-1 px-3 py-1.5 ${propertyMode === "all" ? "bg-primary text-white" : ""}`} aria-pressed={propertyMode === "all"} onClick={() => setPropertyMode("all")}>All Properties</button>
                  <button
                    type="button"
                    className={`flex-1 px-3 py-1.5 text-center ${propertyMode === "individual" ? "bg-primary text-white" : "bg-white text-gray-600 hover:bg-gray-50"}`}
                    onClick={() => { setPropertyMode("individual"); setGroupDropdownOpen(false); }}
                    data-testid="button-property-mode-individual"
                  >
                    Individual Properties
                  </button>
                  <button
                    type="button"
                    className={`flex-1 px-3 py-1.5 text-center border-l ${propertyMode === "group" ? "bg-primary text-white" : "bg-white text-gray-600 hover:bg-gray-50"}`}
                    onClick={() => { setPropertyMode("group"); setPropertyDropdownOpen(false); }}
                    data-testid="button-property-mode-group"
                  >
                    Property Groups
                  </button>
                </div>
                {/* Individual properties selector */}
                <p className="text-xs text-gray-500 mb-2">{propertyMode === "all" ? "All current and future properties, except exclusions below." : "An empty selection sends to no properties."}</p>
                <details className="mb-3">
                  <summary>Excluded properties ({excludedPropertyIds.length})</summary>
                  <p className="text-xs text-gray-500">Exclusions override groups and copied settings. Uncheck a property here to explicitly allow it again.</p>
                  {activeProperties.map(p => <label key={p.id} className="flex gap-2 text-sm py-1">
                    <input type="checkbox" checked={excludedPropertyIds.includes(p.id)} onChange={e => {
                      setExcludedPropertyIds(prev => e.target.checked ? [...new Set([...prev, p.id])] : prev.filter(id => id !== p.id));
                      if (e.target.checked) setSelectedPropertyIds(prev => prev.filter(id => id !== p.id));
                    }} />{p.name || p.address}
                  </label>)}
                </details>
                {propertyMode === "individual" && (
                  <Popover open={propertyDropdownOpen} onOpenChange={open => {
                    setPropertyDropdownOpen(open);
                    if (open) setPropertySearch("");
                  }}>
                    <PopoverTrigger asChild>
                    <button
                      type="button"
                      className="w-full flex items-center justify-between border rounded-md px-3 py-2 text-sm text-left bg-white hover:bg-gray-50"
                      data-testid="button-property-selector"
                    >
                      <span className="text-gray-700">{selectedPropertiesLabel}</span>
                      <ChevronDown className="h-4 w-4 text-gray-400" />
                    </button>
                    </PopoverTrigger>
                      <PopoverContent align="start" className="w-[var(--radix-popover-trigger-width)] max-h-80 overflow-auto bg-white p-0" aria-label="Individual Properties">
                        <div className="p-3">
                          <Label htmlFor="property-search">Search properties</Label>
                          <Input id="property-search" value={propertySearch} onChange={e => setPropertySearch(e.target.value)}
                            placeholder="Search by name or address" data-testid="input-property-search"
                            onKeyDown={e => { if (e.key === "Enter") e.preventDefault(); }} />
                        </div>
                        <button
                          type="button"
                          aria-pressed={activeSelectedIds.length === activeProperties.length && activeProperties.length > 0}
                          disabled={activeProperties.length === 0}
                          className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-gray-50 text-left"
                          onClick={() => {
                            if (activeSelectedIds.length === activeProperties.length) {
                              setSelectedPropertyIds(ids => ids.filter(id => !activePropertyIds.has(id)));
                              setExcludedPropertyIds(ids => [...new Set([...ids, ...activeSelectedIds])]);
                            } else {
                              setSelectedPropertyIds(ids => [...ids.filter(id => !activePropertyIds.has(id)), ...activeProperties.map(p => p.id)]);
                              setExcludedPropertyIds(ids => ids.filter(id => !activePropertyIds.has(id)));
                            }
                          }}
                          data-testid="button-select-all-properties"
                        >
                          <div className={`h-4 w-4 rounded border flex items-center justify-center ${activeSelectedIds.length === activeProperties.length && activeProperties.length > 0 ? "bg-primary border-primary text-white" : "border-gray-300"}`}>
                            {activeSelectedIds.length === activeProperties.length && activeProperties.length > 0 && <Check className="h-3 w-3" />}
                          </div>
                          <span>{normalizedPropertySearch ? "Select All eligible properties (not just search results)" : "Select All"}</span>
                        </button>
                        {filteredProperties.map(p => (
                          <button
                            key={p.id}
                            type="button"
                            aria-pressed={selectedPropertyIds.includes(p.id)}
                            className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-gray-50 text-left"
                            onClick={() => toggleProperty(p.id)}
                            data-testid={`button-property-option-${p.id}`}
                          >
                            <div className={`h-4 w-4 rounded border flex items-center justify-center ${selectedPropertyIds.includes(p.id) ? "bg-primary border-primary text-white" : "border-gray-300"}`}>
                              {selectedPropertyIds.includes(p.id) && <Check className="h-3 w-3" />}
                            </div>
                            <span>
                              <span className="block">{p.name || p.address}</span>
                              {p.address && p.address !== p.name && <span className="block text-xs text-gray-500">{p.address}</span>}
                            </span>
                          </button>
                        ))}
                        {activeProperties.length === 0 && (
                          <div className="px-3 py-2 text-sm text-gray-400">No properties available</div>
                        )}
                        {activeProperties.length > 0 && filteredProperties.length === 0 && (
                          <div className="px-3 py-2 text-sm text-gray-400" role="status">No properties match your search</div>
                        )}
                      </PopoverContent>
                  </Popover>
                )}
                {/* Property groups selector */}
                {propertyMode === "group" && (() => {
                  const groupLabel =
                    selectedGroupIds.length === 0
                      ? "0 groups selected (all properties)"
                      : selectedGroupIds.length === 1
                        ? (groups.find((g: PropertyGroup) => g.id === selectedGroupIds[0])?.name ?? "1 group selected")
                        : `${selectedGroupIds.length} groups selected`;
                  return (
                    <Popover open={groupDropdownOpen} onOpenChange={open => {
                      setGroupDropdownOpen(open);
                      if (open) setGroupSearch("");
                    }}>
                      <PopoverTrigger asChild>
                      <button
                        type="button"
                        className="w-full flex items-center justify-between border rounded-md px-3 py-2 text-sm text-left bg-white hover:bg-gray-50"
                        data-testid="button-group-selector"
                      >
                        <span className="text-gray-700">{groupLabel}</span>
                        <ChevronDown className="h-4 w-4 text-gray-400" />
                      </button>
                      </PopoverTrigger>
                        <PopoverContent align="start" className="w-[var(--radix-popover-trigger-width)] max-h-80 overflow-auto bg-white p-0" aria-label="Property Groups">
                          <div className="p-3">
                            <Label htmlFor="group-search">Search property groups</Label>
                            <Input id="group-search" value={groupSearch} onChange={e => setGroupSearch(e.target.value)}
                              placeholder="Search by group name" data-testid="input-group-search"
                              onKeyDown={e => { if (e.key === "Enter") e.preventDefault(); }} />
                          </div>
                          {groups.length === 0 ? (
                            <div className="px-3 py-3 text-sm text-gray-400">
                              No property groups yet.{" "}
                              <a href="/app/properties" className="text-blue-600 underline" target="_blank" rel="noreferrer">Create one on the Properties page.</a>
                            </div>
                          ) : (
                            filteredGroups.map((g: PropertyGroup) => (
                              <button
                                key={g.id}
                                type="button"
                                aria-pressed={selectedGroupIds.includes(g.id)}
                                className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-gray-50 text-left"
                                onClick={() =>
                                  setSelectedGroupIds(prev =>
                                    prev.includes(g.id) ? prev.filter(id => id !== g.id) : [...prev, g.id]
                                  )
                                }
                                data-testid={`button-group-option-${g.id}`}
                              >
                                <div className={`h-4 w-4 rounded border flex items-center justify-center ${selectedGroupIds.includes(g.id) ? "bg-primary border-primary text-white" : "border-gray-300"}`}>
                                  {selectedGroupIds.includes(g.id) && <Check className="h-3 w-3" />}
                                </div>
                                <span>{g.name}</span>
                              </button>
                            ))
                          )}
                          {groups.length > 0 && filteredGroups.length === 0 && (
                            <div className="px-3 py-2 text-sm text-gray-400" role="status">No property groups match your search</div>
                          )}
                        </PopoverContent>
                    </Popover>
                  );
                })()}
              </div>

              <div data-tour="message-rule-form-rule-type">
                <Label className="text-sm font-medium text-gray-700">Rule Type</Label>
                <div className="mt-2 space-y-2">
                  <label className="flex items-start gap-3 cursor-pointer" data-testid="radio-rule-type-event">
                    <input
                      type="radio"
                      name="ruleType"
                      value="event"
                      checked={ruleType === "event"}
                      onChange={() => setRuleType("event")}
                      className="mt-0.5 accent-primary"
                    />
                    <div>
                      <span className="text-sm font-medium text-gray-900">Event-based message</span>
                      <p className="text-xs text-gray-500">This message will be sent when the selected event occurs.</p>
                    </div>
                  </label>
                  <label className="flex items-start gap-3 cursor-pointer" data-testid="radio-rule-type-scheduled">
                    <input
                      type="radio"
                      name="ruleType"
                      value="scheduled"
                      checked={ruleType === "scheduled"}
                      onChange={() => setRuleType("scheduled")}
                      className="mt-0.5 accent-primary"
                    />
                    <div>
                      <span className="text-sm font-medium text-gray-900">Scheduled message</span>
                      <p className="text-xs text-gray-500">This message will be sent on a set schedule, regardless of events.</p>
                    </div>
                  </label>
                  <label className="flex items-start gap-3 cursor-pointer" data-testid="radio-rule-type-business-hours">
                    <input
                      type="radio"
                      name="ruleType"
                      value="business_hours"
                      checked={ruleType === "business_hours"}
                      onChange={() => setRuleType("business_hours")}
                      className="mt-0.5 accent-primary"
                      disabled={readOnly}
                    />
                    <div>
                      <span className="text-sm font-medium text-gray-900">Business Hours (after-hours auto-reply)</span>
                      <p className="text-xs text-gray-500">Automatically reply to member messages that arrive outside your working hours. Sent at most once per member per closed period.</p>
                    </div>
                  </label>
                  <label
                    className={`flex items-start gap-3 ${keywordRulesGranted ? "cursor-pointer" : "cursor-not-allowed opacity-60"}`}
                    data-testid="radio-rule-type-incoming"
                  >
                    <input
                      type="radio"
                      name="ruleType"
                      value="incoming_keywords"
                      checked={ruleType === "incoming_keywords"}
                      onChange={() => setRuleType("incoming_keywords")}
                      disabled={!keywordRulesGranted || readOnly}
                      className={`mt-0.5 accent-primary ${keywordRulesGranted ? "" : "cursor-not-allowed"}`}
                    />
                    <div>
                      <span className={`text-sm font-medium ${keywordRulesGranted ? "text-gray-900" : "text-gray-500"}`}>
                        Incoming Message (Keywords & Phrases)
                      </span>
                      {keywordRulesGranted ? (
                        <span
                          className="ml-2 inline-block rounded bg-blue-50 border border-blue-200 px-1.5 py-0.5 text-[10px] font-medium text-blue-700 align-middle"
                          data-testid="badge-incoming-beta"
                        >
                          Beta
                        </span>
                      ) : (
                        <span
                          className="ml-2 inline-block rounded bg-gray-100 px-1.5 py-0.5 text-[10px] font-medium text-gray-500 align-middle"
                          data-testid="badge-incoming-coming-soon"
                        >
                          Coming late July 2026
                        </span>
                      )}
                      <p className={`text-xs ${keywordRulesGranted ? "text-gray-500" : "text-gray-400"}`}>
                        This rule is triggered when incoming messages mention selected topics or keywords.
                        {keywordRulesGranted ? " AI reads each incoming message and decides whether it matches — exact wording isn't required. Replies are sent at most once per member per rule each day." : ""}
                      </p>
                    </div>
                  </label>
                </div>
              </div>

              {ruleType === "incoming_keywords" && (
                <div>
                  <Label className="text-sm font-bold text-gray-900">Keywords</Label>
                  <div
                    className="mt-1.5 min-h-[42px] flex flex-wrap gap-1.5 items-center border rounded-md px-3 py-2 bg-white focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-0 cursor-text"
                    onClick={() => keywordInputRef.current?.focus()}
                    data-testid="keywords-picker"
                  >
                    {keywords.map(kw => (
                      <span
                        key={kw}
                        className="inline-flex items-center gap-1 rounded bg-blue-50 border border-blue-200 px-2 py-0.5 text-xs font-medium text-blue-800"
                        data-testid={`keyword-chip-${kw}`}
                      >
                        {kw}
                        {!readOnly && (
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); removeKeyword(kw); }}
                            className="text-blue-500 hover:text-blue-700 ml-0.5"
                            aria-label={`Remove keyword ${kw}`}
                            data-testid={`button-remove-keyword-${kw}`}
                          >
                            <X className="h-3 w-3" />
                          </button>
                        )}
                      </span>
                    ))}
                    <input
                      ref={keywordInputRef}
                      type="text"
                      value={keywordInput}
                      onChange={e => setKeywordInput(e.target.value)}
                      onKeyDown={handleKeywordKeyDown}
                      onBlur={() => addKeyword(keywordInput)}
                      placeholder={keywords.length === 0 ? "Enter a keyword or phrase" : ""}
                      className="flex-1 min-w-[160px] border-none outline-none bg-transparent text-sm placeholder:text-gray-400"
                      disabled={readOnly}
                      data-testid="input-keyword"
                    />
                  </div>
                  <p className="mt-1 text-xs text-gray-500">Press Enter or comma to add. Click × to remove a keyword.</p>
                </div>
              )}

              {ruleType === "business_hours" && (
                <div className="space-y-4 border rounded-md p-4 bg-gray-50" data-testid="section-business-hours">
                  <h3 className="text-sm font-semibold text-gray-800">Working hours</h3>

                  <div className="space-y-2">
                    {BH_DAY_ORDER.map((d) => {
                      const cfg = bhDays[d];
                      return (
                        <div key={d} className="flex items-center gap-3" data-testid={`row-business-hours-${d}`}>
                          <label className="flex items-center gap-2 w-28 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={cfg.open}
                              onChange={(e) => setBhDays((prev) => ({ ...prev, [d]: { ...prev[d], open: e.target.checked } }))}
                              disabled={readOnly}
                              className="accent-primary"
                              data-testid={`checkbox-business-hours-open-${d}`}
                            />
                            <span className="text-sm text-gray-700">{BH_DAY_SHORT[d]}</span>
                          </label>
                          {cfg.open ? (
                            <div className="flex items-center gap-2">
                              <Input
                                type="time"
                                value={cfg.start}
                                onChange={(e) => setBhDays((prev) => ({ ...prev, [d]: { ...prev[d], start: e.target.value } }))}
                                disabled={readOnly}
                                className="w-32"
                                data-testid={`input-business-hours-start-${d}`}
                              />
                              <span className="text-sm text-gray-500">to</span>
                              <Input
                                type="time"
                                value={cfg.end}
                                onChange={(e) => setBhDays((prev) => ({ ...prev, [d]: { ...prev[d], end: e.target.value } }))}
                                disabled={readOnly}
                                className="w-32"
                                data-testid={`input-business-hours-end-${d}`}
                              />
                            </div>
                          ) : (
                            <span className="text-sm text-gray-400" data-testid={`text-business-hours-closed-${d}`}>Closed</span>
                          )}
                          {bhErrors[d] && (
                            <p className="text-xs text-red-600" data-testid={`text-error-business-hours-${d}`}>{bhErrors[d]}</p>
                          )}
                        </div>
                      );
                    })}
                    {bhErrors.days && (
                      <p className="text-xs text-red-600" data-testid="text-error-business-hours-days">{bhErrors.days}</p>
                    )}
                  </div>

                  <p className="text-xs text-gray-600" data-testid="text-business-hours-summary">
                    {summarizeBusinessHours(bhDays)}
                  </p>

                  <div>
                    <Label className="text-sm font-medium text-gray-700">Time zone</Label>
                    <Select
                      value={bhTimezone || "__account__"}
                      onValueChange={(v) => setBhTimezone(v === "__account__" ? "" : v)}
                    >
                      <SelectTrigger className="mt-1.5" data-testid="select-business-hours-timezone">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__account__">Account time zone (automatic)</SelectItem>
                        {ACCOUNT_TIMEZONES.map(opt => (
                          <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-gray-500 mt-1">Defaults to your account time zone. Pick a specific zone to override.</p>
                  </div>

                  <div>
                    <Label className="text-sm font-medium text-gray-700">Active date range (optional)</Label>
                    <div className="mt-1.5 flex items-center gap-2">
                      <Input
                        type="date"
                        value={bhStartDate}
                        onChange={(e) => setBhStartDate(e.target.value)}
                        disabled={readOnly}
                        className="w-44"
                        data-testid="input-business-hours-start-date"
                      />
                      <span className="text-sm text-gray-500">to</span>
                      <Input
                        type="date"
                        value={bhEndDate}
                        onChange={(e) => setBhEndDate(e.target.value)}
                        disabled={readOnly}
                        className="w-44"
                        data-testid="input-business-hours-end-date"
                      />
                    </div>
                    {bhErrors.dateRange && (
                      <p className="text-xs text-red-600 mt-1" data-testid="text-error-business-hours-date-range">{bhErrors.dateRange}</p>
                    )}
                    <p className="text-xs text-gray-500 mt-1">Leave blank to keep the rule active year-round. Outside this range the auto-reply never sends.</p>
                  </div>
                </div>
              )}

              {ruleType === "event" && (
                <div>
                  <Label className="text-sm font-medium text-gray-700">Event Trigger</Label>
                  <Select value={eventTrigger} onValueChange={setEventTrigger}>
                    <SelectTrigger className="mt-1.5" data-testid="select-event-trigger">
                      <SelectValue placeholder="Select an option" />
                    </SelectTrigger>
                    <SelectContent>
                      {EVENT_TRIGGER_OPTIONS.map(opt => (
                        <SelectItem key={opt.value} value={opt.value} data-testid={`option-event-trigger-${opt.value}`}>
                          {opt.comingSoon ? `${opt.label} - Coming soon` : opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {isScheduled && (
                <div className="space-y-4 border rounded-md p-4 bg-gray-50">
                  <h3 className="text-sm font-semibold text-gray-800">Schedule</h3>

                  <div>
                    <Label className="text-sm font-medium text-gray-700">Frequency</Label>
                    <Select
                      value={scheduleFrequency}
                      onValueChange={v => {
                        setScheduleFrequency(v);
                        setScheduleDayOfWeek("");
                        setScheduleWeekOfMonth("");
                        setScheduleDate("");
                        clearScheduleErrors("scheduleFrequency");
                        clearScheduleErrors("scheduleDayOfWeek");
                        clearScheduleErrors("scheduleWeekOfMonth");
                        clearScheduleErrors("scheduleDate");
                      }}
                    >
                      <SelectTrigger className="mt-1.5" data-testid="select-schedule-frequency">
                        <SelectValue placeholder="Select an option" />
                      </SelectTrigger>
                      <SelectContent>
                        {FREQUENCY_OPTIONS.map(opt => (
                          <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {scheduleErrors.scheduleFrequency && (
                      <p className="text-xs text-red-600 mt-1" data-testid="text-error-schedule-frequency">{scheduleErrors.scheduleFrequency}</p>
                    )}
                  </div>

                  {needsWeek && (
                    <div>
                      <Label className="text-sm font-medium text-gray-700">Week</Label>
                      <Select
                        value={scheduleWeekOfMonth}
                        onValueChange={v => {
                          setScheduleWeekOfMonth(v);
                          clearScheduleErrors("scheduleWeekOfMonth");
                        }}
                      >
                        <SelectTrigger className="mt-1.5" data-testid="select-schedule-week">
                          <SelectValue placeholder="Select an option" />
                        </SelectTrigger>
                        <SelectContent>
                          {WEEK_OPTIONS.map(opt => (
                            <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {scheduleErrors.scheduleWeekOfMonth && (
                        <p className="text-xs text-red-600 mt-1" data-testid="text-error-schedule-week">{scheduleErrors.scheduleWeekOfMonth}</p>
                      )}
                    </div>
                  )}

                  {needsDay && (
                    <div>
                      <Label className="text-sm font-medium text-gray-700">Day</Label>
                      <Select
                        value={scheduleDayOfWeek}
                        onValueChange={v => {
                          setScheduleDayOfWeek(v);
                          clearScheduleErrors("scheduleDayOfWeek");
                        }}
                      >
                        <SelectTrigger className="mt-1.5" data-testid="select-schedule-day">
                          <SelectValue placeholder="Select an option" />
                        </SelectTrigger>
                        <SelectContent>
                          {DAY_OPTIONS.map(opt => (
                            <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {scheduleErrors.scheduleDayOfWeek && (
                        <p className="text-xs text-red-600 mt-1" data-testid="text-error-schedule-day">{scheduleErrors.scheduleDayOfWeek}</p>
                      )}
                    </div>
                  )}

                  {needsDate && (
                    <div>
                      <Label className="text-sm font-medium text-gray-700">Date</Label>
                      <Input
                        type="date"
                        value={scheduleDate}
                        onChange={e => {
                          setScheduleDate(e.target.value);
                          clearScheduleErrors("scheduleDate");
                        }}
                        className="mt-1.5"
                        data-testid="input-schedule-date"
                      />
                      {scheduleErrors.scheduleDate && (
                        <p className="text-xs text-red-600 mt-1" data-testid="text-error-schedule-date">{scheduleErrors.scheduleDate}</p>
                      )}
                    </div>
                  )}

                  <div>
                    <Label className="text-sm font-medium text-gray-700">Time</Label>
                    <Input
                      type="time"
                      value={scheduleTime}
                      onChange={e => {
                        setScheduleTime(e.target.value);
                        clearScheduleErrors("scheduleTime");
                      }}
                      className="mt-1.5"
                      data-testid="input-schedule-time"
                    />
                    {scheduleErrors.scheduleTime && (
                      <p className="text-xs text-red-600 mt-1" data-testid="text-error-schedule-time">{scheduleErrors.scheduleTime}</p>
                    )}
                  </div>

                  <div>
                    <Label className="text-sm font-medium text-gray-700">Time zone</Label>
                    <Select
                      value={scheduleTimezone || "__property__"}
                      onValueChange={v => setScheduleTimezone(v === "__property__" ? "" : v)}
                    >
                      <SelectTrigger className="mt-1.5" data-testid="select-schedule-timezone">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__property__">Property's time zone (automatic)</SelectItem>
                        {ACCOUNT_TIMEZONES.map(opt => (
                          <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-gray-500 mt-1">Defaults to each property's own time zone. Pick a specific zone to override for all selected properties.</p>
                  </div>
                </div>
              )}

              <div>
                <Label className="text-sm font-medium text-gray-700">Message recipients</Label>
                {isScheduled ? (
                  <p className="mt-2 text-sm text-gray-700" data-testid="text-recipients-scheduled">
                    Scheduled messages are sent to all members of the selected properties.
                  </p>
                ) : ruleType === "business_hours" ? (
                  <p className="mt-2 text-sm text-gray-700" data-testid="text-recipients-business-hours">
                    The auto-reply goes to the member who messaged you, on the same channel they used.
                  </p>
                ) : (
                  <div className="mt-2 space-y-2">
                    <label className="flex items-center gap-3 cursor-pointer" data-testid="radio-recipients-trigger">
                      <input
                        type="radio"
                        name="messageRecipients"
                        value="trigger_person"
                        checked={messageRecipients === "trigger_person"}
                        onChange={() => setMessageRecipients("trigger_person")}
                        className="accent-primary"
                      />
                      <span className="text-sm text-gray-700">Only the person who triggered the event</span>
                    </label>
                    <label className="flex items-center gap-3 cursor-pointer" data-testid="radio-recipients-all">
                      <input
                        type="radio"
                        name="messageRecipients"
                        value="all_members"
                        checked={messageRecipients === "all_members"}
                        onChange={() => setMessageRecipients("all_members")}
                        className="accent-primary"
                      />
                      <span className="text-sm text-gray-700">All members of that person's property</span>
                    </label>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="space-y-6">
            <div className="border rounded-lg p-6 bg-white">
              <div className="space-y-5">
                <div>
                  {/* Task #4813 — event rules can source their message from a
                      saved template (live link: template edits apply to this
                      rule automatically). */}
                  {ruleType === "event" && !readOnly && (cannedResponsesQuery.data?.length ?? 0) > 0 && (
                    <div className="mb-4">
                      <Label className="text-sm font-medium text-gray-700">Message source</Label>
                      <Select
                        value={cannedResponseId != null ? String(cannedResponseId) : "inline"}
                        onValueChange={(v) => setCannedResponseId(v === "inline" ? null : parseInt(v, 10))}
                      >
                        <SelectTrigger className="mt-1.5" data-testid="select-message-template">
                          <SelectValue placeholder="Write a message below" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="inline">Write a message below</SelectItem>
                          {(cannedResponsesQuery.data ?? []).map((c) => (
                            <SelectItem key={c.id} value={String(c.id)}>
                              Template: {c.title}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {cannedResponseId != null && (
                        <p className="mt-1.5 text-xs text-gray-500" data-testid="text-template-live-note">
                          This rule sends the template’s current text. Edits you make to the template later apply to this rule automatically. If the template is deleted or archived, the fallback message below is sent instead.
                        </p>
                      )}
                    </div>
                  )}
                  {linkedTemplate && (
                    <div className="mb-4 rounded-md border bg-gray-50 p-3" data-testid="preview-linked-template">
                      <p className="text-xs font-medium text-gray-500 mb-1">Template “{linkedTemplate.title}”</p>
                      <p className="text-sm whitespace-pre-wrap">{linkedTemplate.body}</p>
                    </div>
                  )}
                  <div className="flex items-center justify-between">
                    <Label className="text-sm font-medium text-gray-700">
                      {cannedResponseId != null ? "Fallback message (optional)" : "Message Content"}
                    </Label>
                    {!readOnly && (
                      <InsertPlaceholderDropdown
                        onInsert={insertPlaceholderIntoMessageContent}
                        extraOptions={bookingLinkPlaceholderOptions}
                      />
                    )}
                  </div>
                  <Textarea
                    ref={messageContentRef}
                    value={messageContent}
                    onChange={e => setMessageContent(e.target.value)}
                    placeholder={cannedResponseId != null ? "Optional fallback if the template is removed…" : "Enter your message content..."}
                    rows={5}
                    className="mt-1.5"
                    data-testid="input-message-content"
                    data-tour="message-rule-form-template"
                  />
                  <MessagePlaceholderPreview
                    content={effectiveMessageContent}
                    recipientContext={
                      previewMember
                        ? {
                            memberFirstName: previewMember.firstName,
                            memberFullName: previewMember.fullName,
                          }
                        : null
                    }
                    recipientName={previewMember?.fullName ?? null}
                  />
                </div>

                <div>
                  <Label className="text-sm font-medium text-gray-700">Attachment (optional)</Label>
                  <p className="text-xs text-gray-500 mt-0.5">
                    Attach one image (max 10MB) to include with every message this rule sends.
                  </p>
                  {attachmentPreviewSrc || attachmentImageUrl ? (
                    <div
                      className="mt-2 flex items-center gap-3 rounded-md border p-2"
                      data-testid="rule-attachment-preview"
                    >
                      <img
                        src={attachmentPreviewSrc || attachmentImageUrl}
                        alt={attachmentName || "Attached image"}
                        className="h-14 w-14 rounded object-cover bg-gray-100"
                        data-testid="img-rule-attachment"
                      />
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm text-gray-700" data-testid="text-rule-attachment-name">
                          {attachmentName || "Attached image"}
                        </p>
                        {attachmentUploading && (
                          <p className="flex items-center gap-1 text-xs text-gray-500" data-testid="text-rule-attachment-uploading">
                            <Loader2 className="h-3 w-3 animate-spin" /> Uploading…
                          </p>
                        )}
                      </div>
                      {!readOnly && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={removeAttachment}
                          aria-label="Remove attachment"
                          data-testid="button-remove-rule-attachment"
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  ) : (
                    !readOnly && (
                      <Button
                        type="button"
                        variant="outline"
                        className="mt-2"
                        onClick={() => attachmentInputRef.current?.click()}
                        disabled={attachmentUploading}
                        data-testid="button-add-rule-attachment"
                      >
                        {attachmentUploading ? (
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        ) : (
                          <Paperclip className="mr-2 h-4 w-4" />
                        )}
                        Attach image
                      </Button>
                    )
                  )}
                  <input
                    ref={attachmentInputRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={e => handleAttachmentFile(e.target.files)}
                    data-testid="input-rule-attachment-file"
                  />
                  {attachmentError && (
                    <p className="text-xs text-red-600 mt-1.5" data-testid="text-rule-attachment-error">
                      {attachmentError}
                    </p>
                  )}
                </div>

              </div>
            </div>
          </div>
        </div>

        {ruleType === "incoming_keywords" && keywordRulesGranted && (
          <div className="border rounded-lg p-6 bg-white mt-6" data-testid="incoming-test-playground">
            <h2 className="text-lg font-semibold text-gray-900 mb-1" data-testid="text-playground-heading">
              Test This Rule
            </h2>
            <p className="text-sm text-gray-500 mb-4">
              Send a pretend message the way a member would and see whether this rule matches and the exact
              reply it would create. Tests never message anyone — replies here are previews only.
            </p>

            <div className="flex items-start gap-2">
              <Textarea
                value={testMessage}
                onChange={e => setTestMessage(e.target.value)}
                onKeyDown={e => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    if (!testMutation.isPending) handleRunTest();
                  }
                }}
                placeholder='Type a message as a member… e.g. "hey, what’s the wifi password?"'
                rows={2}
                className="flex-1"
                disabled={readOnly || testMutation.isPending}
                data-testid="input-test-message"
              />
              <Button
                type="button"
                onClick={handleRunTest}
                disabled={readOnly || testMutation.isPending || !testMessage.trim()}
                data-testid="button-run-test"
              >
                {testMutation.isPending ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Testing…
                  </>
                ) : (
                  "Test"
                )}
              </Button>
            </div>

            {testHistory.length > 0 && (
              <div className="mt-5 space-y-5" data-testid="test-history">
                {testHistory.map((entry, i) => (
                  <div key={`${i}-${entry.message}`} className="border rounded-md p-4 bg-gray-50" data-testid={`test-result-${i}`}>
                    {/* Simulated inbound bubble (member side, left) */}
                    <div className="flex justify-start">
                      <div className="max-w-[80%] rounded-lg bg-white border px-3 py-2 text-sm whitespace-pre-wrap break-words text-gray-800" data-testid={`text-test-message-${i}`}>
                        {entry.message}
                      </div>
                    </div>

                    {/* Verdict */}
                    <div className="mt-3 flex items-center gap-2 flex-wrap">
                      {entry.result.matched ? (
                        <span
                          className="inline-flex items-center gap-1 rounded-full bg-green-100 text-green-800 px-2.5 py-0.5 text-xs font-medium"
                          data-testid={`chip-test-verdict-${i}`}
                        >
                          <Check className="h-3 w-3" />
                          Would match ({Math.round(entry.result.confidence * 100)}% confident)
                        </span>
                      ) : (
                        <span
                          className="inline-flex items-center gap-1 rounded-full bg-gray-200 text-gray-700 px-2.5 py-0.5 text-xs font-medium"
                          data-testid={`chip-test-verdict-${i}`}
                        >
                          <X className="h-3 w-3" />
                          Would not match
                        </span>
                      )}
                      {entry.result.reason && (
                        <span className="text-xs text-gray-500" data-testid={`text-test-reason-${i}`}>
                          {entry.result.reason}
                        </span>
                      )}
                      {entry.result.skippedNoContent && (
                        <span className="text-xs text-gray-500 italic" data-testid={`text-test-skipped-${i}`}>
                          This message has no real content (greetings and thank-yous are ignored automatically).
                        </span>
                      )}
                    </div>

                    {/* Competing-rule warning */}
                    {entry.result.otherMatchingRules.length > 0 && (
                      <div
                        className="mt-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800"
                        data-testid={`warning-other-rules-${i}`}
                      >
                        {entry.result.otherMatchingRules.map(r => (
                          <p key={r.id}>
                            Your rule "{r.name}" would also match this message ({Math.round(r.confidence * 100)}% confident).
                            When rules compete, the most confident match replies.
                          </p>
                        ))}
                      </div>
                    )}

                    {/* Rendered reply bubble (host side, right) */}
                    {entry.result.matched && entry.result.renderedReply && (
                      <div className="mt-3">
                        <p className="text-[11px] text-gray-500 mb-1">
                          Reply this rule would create (as a draft for your review, previewed with sample member data):
                        </p>
                        <div className="flex justify-end">
                          <div
                            className="max-w-[80%] rounded-lg bg-blue-500 text-white px-3 py-2 text-sm whitespace-pre-wrap break-words shadow-sm max-h-64 overflow-y-auto"
                            data-testid={`text-test-reply-${i}`}
                          >
                            {entry.result.renderedReply}
                          </div>
                        </div>
                      </div>
                    )}
                    {entry.result.matched && !entry.result.renderedReply && (
                      <p className="mt-3 text-xs italic text-gray-500" data-testid={`text-test-no-content-${i}`}>
                        This rule matches, but it has no message content yet — add content above to see the reply.
                      </p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        <div className="border rounded-lg p-6 bg-white mt-6" data-tour="message-rule-form-approval">
          <h2 className="text-lg font-semibold text-gray-900 mb-1" data-testid="text-approval-heading">
            Message Approval
          </h2>
          <p className="text-sm text-gray-500 mb-4">
            Choose whether messages are sent automatically or reviewed before sending, and when they should be delivered.
          </p>

          <div className="space-y-3">
            <label className="flex items-start gap-3 cursor-pointer" data-testid="radio-approval-auto">
              <input
                type="radio"
                name="messageApproval"
                value="send_automatically"
                checked={messageApproval === "send_automatically"}
                onChange={() => setMessageApproval("send_automatically")}
                className="mt-0.5 accent-primary"
              />
              <div>
                <span className="text-sm font-medium text-gray-900">Send automatically</span>
                <p className="text-xs text-gray-500">Send the message without manual approval.</p>
              </div>
            </label>

            {messageApproval === "send_automatically" && !isScheduled && isBookingFormFollowup && (
              <div className="ml-7 border-l-2 border-gray-200 pl-4" data-testid="text-followup-fixed-timing">
                <p className="text-xs text-gray-500">
                  Timing is automatic for this trigger: the reminder is sent 2 hours before the
                  property's booking auto-approval deadline. The auto-approval window (24 or 48
                  hours) is set individually per property on the Properties page.
                </p>
              </div>
            )}
            {messageApproval === "send_automatically" && !isScheduled && !isBookingFormFollowup && (
              <div className="ml-7 space-y-2 border-l-2 border-gray-200 pl-4">
                <label className="flex items-start gap-3 cursor-pointer" data-testid="radio-timing-immediately">
                  <input
                    type="radio"
                    name="sendTiming"
                    value="immediately"
                    checked={sendTiming === "immediately"}
                    onChange={() => setSendTiming("immediately")}
                    className="mt-0.5 accent-primary"
                  />
                  <div>
                    <span className="text-sm font-medium text-gray-900">Send immediately</span>
                    <p className="text-xs text-gray-500">Send the message the moment the trigger occurs.</p>
                  </div>
                </label>
                <label className="flex items-start gap-3 cursor-pointer" data-testid="radio-timing-delay">
                  <input
                    type="radio"
                    name="sendTiming"
                    value="after_delay"
                    checked={sendTiming === "after_delay"}
                    onChange={() => {
                      setSendTiming("after_delay");
                      setDelayError("");
                    }}
                    className="mt-0.5 accent-primary"
                  />
                  <div>
                    <span className="text-sm font-medium text-gray-900">After a Delay</span>
                    <p className="text-xs text-gray-500">Choose how long after the event this message should be sent.</p>
                  </div>
                </label>
                {sendTiming === "after_delay" && (
                  <div className="ml-7 mt-2 space-y-2" data-testid="delay-control">
                    <div className="flex flex-wrap gap-2">
                      {[
                        { label: "5 min", value: "5", unit: "minutes" as const },
                        { label: "15 min", value: "15", unit: "minutes" as const },
                        { label: "30 min", value: "30", unit: "minutes" as const },
                        { label: "1 hour", value: "60", unit: "hours" as const },
                        { label: "2 hours", value: "120", unit: "hours" as const },
                        { label: "Custom", value: "custom", unit: null },
                      ].map(opt => (
                        <button
                          key={opt.value}
                          type="button"
                          onClick={() => {
                            setDelayPreset(opt.value);
                            setDelayError("");
                            if (opt.value !== "custom") {
                              setDelayMinutes(Number(opt.value));
                              if (opt.unit) setDelayUnit(opt.unit);
                            } else {
                              setDelayMinutes(null);
                            }
                          }}
                          className={`px-3 py-1 rounded-md text-xs font-medium border transition-colors ${delayPreset === opt.value ? "bg-primary text-white border-primary" : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"}`}
                          data-testid={`button-delay-preset-${opt.value}`}
                        >
                          {opt.label}
                        </button>
                      ))}
                    </div>
                    {delayPreset === "custom" && (() => {
                      const unitFactor = delayUnit === "days" ? 1440 : delayUnit === "hours" ? 60 : 1;
                      const displayValue = delayMinutes != null ? delayMinutes / unitFactor : "";
                      return (
                        <div className="flex items-center gap-2 mt-2">
                          <Input
                            type="number"
                            min={1}
                            value={displayValue}
                            onChange={e => {
                              if (e.target.value === "") {
                                setDelayMinutes(null);
                              } else {
                                const parsed = parseInt(e.target.value, 10);
                                setDelayMinutes(isNaN(parsed) ? null : parsed * unitFactor);
                              }
                              setDelayError("");
                            }}
                            placeholder={`Enter ${delayUnit}`}
                            className="w-24 h-8 text-sm"
                            data-testid="input-delay-custom"
                          />
                          <Select
                            value={delayUnit}
                            onValueChange={(val: "minutes" | "hours" | "days") => {
                              setDelayUnit(val);
                              setDelayError("");
                            }}
                          >
                            <SelectTrigger className="h-8 w-28 text-sm" data-testid="select-delay-unit">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="minutes">Minutes</SelectItem>
                              <SelectItem value="hours">Hours</SelectItem>
                              <SelectItem value="days">Days</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      );
                    })()}
                    {delayError && (
                      <p className="text-xs text-red-600 mt-1" data-testid="text-delay-error">{delayError}</p>
                    )}
                  </div>
                )}
              </div>
            )}

            {messageApproval === "send_automatically" && isScheduled && (
              <div className="ml-7 border-l-2 border-gray-200 pl-4">
                <p className="text-xs text-gray-500 py-2">
                  Delivery time is controlled by the Frequency and Time settings above.
                </p>
              </div>
            )}

            {messageApproval === "send_automatically" && !isScheduled && ruleType !== "business_hours" && (
              <div className="ml-7 border-l-2 border-gray-200 pl-4 py-2 space-y-2" data-testid="section-rule-quiet-hours">
                <label className="flex items-center gap-2 text-sm text-gray-900 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={quietEnabled}
                    onChange={(e) => setQuietEnabled(e.target.checked)}
                    className="accent-primary"
                    data-testid="checkbox-rule-quiet-hours"
                  />
                  Hold auto-sends during quiet hours
                </label>
                {quietEnabled && (
                  <div className="flex flex-wrap items-center gap-2 text-sm text-gray-700">
                    <span>From</span>
                    <input
                      type="time"
                      value={quietStart}
                      onChange={(e) => setQuietStart(e.target.value)}
                      className="rounded-md border border-gray-300 px-2 py-1 text-sm"
                      aria-label="Quiet hours start"
                      data-testid="input-rule-quiet-start"
                    />
                    <span>to</span>
                    <input
                      type="time"
                      value={quietEnd}
                      onChange={(e) => setQuietEnd(e.target.value)}
                      className="rounded-md border border-gray-300 px-2 py-1 text-sm"
                      aria-label="Quiet hours end"
                      data-testid="input-rule-quiet-end"
                    />
                  </div>
                )}
                <p className="text-xs text-gray-500">
                  Uses your account timezone. A message that would go out in this window waits until it ends.
                  Messages about an emergency, safety, police or medical issue are never sent automatically.
                </p>
                {quietEnabled && quietStart === quietEnd && (
                  <p className="text-xs text-red-600" data-testid="text-quiet-hours-error">Start and end must be different.</p>
                )}
              </div>
            )}

            <label className="flex items-start gap-3 cursor-pointer" data-testid="radio-approval-review">
              <input
                type="radio"
                name="messageApproval"
                value="review_before_sending"
                checked={messageApproval === "review_before_sending"}
                onChange={() => {
                  setMessageApproval("review_before_sending");
                  setSendTiming("immediately");
                  setDelayPreset("");
                  setDelayMinutes(null);
                  setDelayError("");
                }}
                className="mt-0.5 accent-primary"
              />
              <div>
                <span className="text-sm font-medium text-gray-900">Review before sending</span>
                <p className="text-xs text-gray-500">Drafts will be created and sent to you via email or SMS for review. After reviewing, you can either send them directly from your email or edit and send them from the PadPulse dashboard.</p>
              </div>
            </label>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
